import { apiClient, API_CONFIG } from '@/api/core';
import type { ApiResponse, ApiSortParams, LC_PagedResult } from '@/api/shared';
import { buildQueryString } from '@/api/shared';
import type {
  LC_HolidayItemOut,
  LC_CreateHolidayIn,
  LC_CreateHolidayOut,
  LC_UpdateHolidayIn,
  LC_UpdateHolidayOut,
  LC_DeleteHolidayOut,
  HolidayListPage,
  HolidayListParams,
  LC_HolidayByRegionItemOut,
  HolidayByRegionListPage,
  HolidayByRegionListParams,
  LC_CreateHolidayByRegionIn,
  LC_CreateHolidayByRegionOut,
  LC_UpdateHolidayByRegionIn,
  LC_UpdateHolidayByRegionOut,
  LC_DeleteHolidayByRegionOut,
  HolidayByRegionItem,
  LC_BulkCreateHolidayIn,
  LC_BulkCreateHolidayOut,
} from '../types/holiday.types';
import { mapHolidayItem, mapHolidayByRegionItem } from '../mappers/holiday.mapper';


export interface HolidayExportParams {
  searchText?: string;
  sort?: ApiSortParams;
  year?: number;
  month?: number;
  holidayTypeId?: number;
}

export interface HolidayByRegionExportParams {
  searchText?: string;
  sort?: ApiSortParams;
  year?: number;
  month?: number;
  regionId?: number;
  holidayTypeId?: number;
}


export class HolidayService {
  async list(
    params: HolidayListParams,
    signal?: AbortSignal,
  ): Promise<ApiResponse<HolidayListPage>> {
    const queryString = buildQueryString({
      page: params.page,
      pageSize: params.pageSize,
      searchText: params.searchText,
      orderBy: params.sort?.key,
      orderDirection: params.sort?.direction?.toUpperCase(),
      year: params.year,
      month: params.month,
      holidayTypeId: params.holidayTypeId,
    });
    const endpoint = `${API_CONFIG.ENDPOINTS.HOLIDAYS.LIST}${queryString}`;

    const response = await apiClient.get<LC_PagedResult<LC_HolidayItemOut>>(endpoint, { signal });

    if (response.success && response.data) {
      return {
        success: true,
        data: {
          items: (response.data.data ?? []).map(mapHolidayItem),
          totalRecords: response.data.pagination.totalRecords,
        },
      };
    }

    return { success: false, data: null, error: response.error };
  }

  async create(data: LC_CreateHolidayIn): Promise<ApiResponse<LC_CreateHolidayOut>> {
    const endpoint = API_CONFIG.ENDPOINTS.HOLIDAYS.LIST;
    const response = await apiClient.post<LC_CreateHolidayOut>(endpoint, data);

    if (response.success && response.data) {
      return { success: true, data: response.data };
    }

    return { success: false, data: null, error: response.error };
  }

  async update(id: number, data: LC_UpdateHolidayIn): Promise<ApiResponse<LC_UpdateHolidayOut>> {
    const endpoint = API_CONFIG.ENDPOINTS.HOLIDAYS.BY_ID(id);
    const response = await apiClient.put<LC_UpdateHolidayOut>(endpoint, data);

    if (response.success && response.data) {
      return { success: true, data: response.data };
    }

    return { success: false, data: null, error: response.error };
  }

  async delete(id: number, signal?: AbortSignal): Promise<ApiResponse<LC_DeleteHolidayOut>> {
    const endpoint = API_CONFIG.ENDPOINTS.HOLIDAYS.BY_ID(id);
    const response = await apiClient.delete<LC_DeleteHolidayOut>(endpoint, undefined, { signal });

    if (response.success && response.data) {
      return { success: true, data: response.data };
    }

    return { success: false, data: null, error: response.error };
  }

  async listByRegion(
    params: HolidayByRegionListParams,
    signal?: AbortSignal,
  ): Promise<ApiResponse<HolidayByRegionListPage>> {
    const queryString = buildQueryString({
      page: params.page,
      pageSize: params.pageSize,
      searchText: params.searchText,
      orderBy: params.sort?.key,
      orderDirection: params.sort?.direction?.toUpperCase(),
      year: params.year,
      month: params.month,
      regionId: params.regionId,
      holidayTypeId: params.holidayTypeId,
    });
    const endpoint = `${API_CONFIG.ENDPOINTS.HOLIDAYS.REGION_LIST}${queryString}`;

    const response = await apiClient.get<LC_PagedResult<LC_HolidayByRegionItemOut>>(
      endpoint,
      { signal },
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: {
          items: (response.data.data ?? []).map(mapHolidayByRegionItem),
          totalRecords: response.data.pagination.totalRecords,
        },
      };
    }

    return { success: false, data: null, error: response.error };
  }

  async createByRegion(
    data: LC_CreateHolidayByRegionIn,
  ): Promise<ApiResponse<HolidayByRegionItem>> {
    const endpoint = API_CONFIG.ENDPOINTS.HOLIDAYS.REGION_LIST;
    const response = await apiClient.post<LC_CreateHolidayByRegionOut>(endpoint, data);

    if (response.success && response.data) {
      return { success: true, data: mapHolidayByRegionItem(response.data) };
    }

    return { success: false, data: null, error: response.error };
  }

  async updateByRegion(
    id: number,
    data: LC_UpdateHolidayByRegionIn,
  ): Promise<ApiResponse<HolidayByRegionItem>> {
    const endpoint = API_CONFIG.ENDPOINTS.HOLIDAYS.REGION_BY_ID(id);
    const response = await apiClient.put<LC_UpdateHolidayByRegionOut>(endpoint, data);

    if (response.success && response.data) {
      return { success: true, data: mapHolidayByRegionItem(response.data) };
    }

    return { success: false, data: null, error: response.error };
  }

  async deleteByRegion(id: number): Promise<ApiResponse<LC_DeleteHolidayByRegionOut>> {
    const endpoint = API_CONFIG.ENDPOINTS.HOLIDAYS.REGION_BY_ID(id);
    const response = await apiClient.delete<LC_DeleteHolidayByRegionOut>(endpoint);

    if (response.success && response.data) {
      return { success: true, data: response.data };
    }

    return { success: false, data: null, error: response.error };
  }

  async bulkCreate(data: LC_BulkCreateHolidayIn): Promise<ApiResponse<LC_BulkCreateHolidayOut>> {
    const response = await apiClient.post<LC_BulkCreateHolidayOut>(
      API_CONFIG.ENDPOINTS.HOLIDAYS.BULK,
      data,
    );

    if (response.success && response.data) {
      return { success: true, data: response.data };
    }

    return { success: false, data: null, error: response.error };
  }

  async exportFile(
    params?: HolidayExportParams,
  ): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    const queryString = buildQueryString({
      searchText: params?.searchText,
      orderBy: params?.sort?.key,
      orderDirection: params?.sort?.direction,
      year: params?.year,
      month: params?.month,
      holidayTypeId: params?.holidayTypeId,
    });
    return apiClient.downloadBlobWithFilename(
      `${API_CONFIG.ENDPOINTS.HOLIDAYS.EXPORT}${queryString}`,
      { method: 'GET' },
    );
  }

  async exportByRegionFile(
    params?: HolidayByRegionExportParams,
  ): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    const queryString = buildQueryString({
      searchText: params?.searchText,
      orderBy: params?.sort?.key,
      orderDirection: params?.sort?.direction,
      year: params?.year,
      month: params?.month,
      regionId: params?.regionId,
      holidayTypeId: params?.holidayTypeId,
    });
    return apiClient.downloadBlobWithFilename(
      `${API_CONFIG.ENDPOINTS.HOLIDAYS.REGION_EXPORT}${queryString}`,
      { method: 'GET' },
    );
  }
}

export const holidayService = new HolidayService();
